import { useState, useEffect } from "react";
import { Download, Loader, RefreshCw } from "lucide-react";
import Modal from "../../components/Modal";
import { clientsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useSettings } from "@/context/SettingsContext";
import { formatCurrency } from "@/lib/data";
import { Client, LedgerData } from "../types";
import PrintableLedger from "./PrintableLedger";

interface ClientLedgerModalProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ClientLedgerModal({ client, isOpen, onClose }: ClientLedgerModalProps) {
  const { showToast } = useToast();
  const { settings } = useSettings();

  const [ledgerData, setLedgerData] = useState<LedgerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [printLedger, setPrintLedger] = useState<LedgerData | null>(null);

  const loadLedger = async (clientId: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      const response = await clientsApi.getLedger(clientId, params.toString());
      setLedgerData(response.data?.data || response.data);
    } catch {
      showToast("Failed to load ledger", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && client) loadLedger(client.id);
  }, [isOpen, client]);

  const handleClose = () => {
    setLedgerData(null);
    setFromDate("");
    setToDate("");
    onClose();
  };

  const downloadPdf = async () => {
    if (!client || !ledgerData) return;
    setPdfGenerating(true);
    try {
      const fromDateFormatted = fromDate ? new Date(fromDate).toLocaleDateString("en-IN") : "Beginning";
      const toDateFormatted = toDate ? new Date(toDate).toLocaleDateString("en-IN") : "Now";

      setPrintLedger({
        ...ledgerData,
        entries: ledgerData.entries.map(e => ({
          ...e,
          debit: e.debit,
          credit: e.credit,
        })),
        fromDate: fromDateFormatted,
        toDate: toDateFormatted,
        generatedAt: new Date().toLocaleDateString("en-IN"),
      } as any);

      setTimeout(() => {
        window.print();
        setTimeout(() => setPrintLedger(null), 100);
      }, 100);

      showToast("Ledger ready — use browser's Save as PDF", "success");
    } catch {
      showToast("Failed to generate ledger", "error");
    } finally {
      setPdfGenerating(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={client ? `${client.name} — Ledger` : "Client Ledger"}
        size="lg"
      >
        <div className="space-y-4">
          {/* Date filter row */}
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs text-stone-500 mb-1">From Date</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm outline-none focus:border-amber-400" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-stone-500 mb-1">To Date</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm outline-none focus:border-amber-400" />
            </div>
            <button
              onClick={() => client && loadLedger(client.id)}
              className="flex items-center gap-1.5 px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-xl text-sm font-medium transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Apply
            </button>
          </div>

          {/* Summary cards */}
          {ledgerData?.summary && (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-red-50 rounded-xl p-3 border border-red-100">
                <div className="text-xs text-red-600 mb-0.5">Total Invoiced</div>
                <div className="font-bold text-red-700">{formatCurrency(ledgerData.summary.total_invoiced)}</div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                <div className="text-xs text-emerald-600 mb-0.5">Total Paid</div>
                <div className="font-bold text-emerald-700">{formatCurrency(ledgerData.summary.total_paid)}</div>
              </div>
              <div className={`rounded-xl p-3 border ${ledgerData.summary.current_balance > 0 ? "bg-amber-50 border-amber-100" : "bg-emerald-50 border-emerald-100"}`}>
                <div className={`text-xs mb-0.5 ${ledgerData.summary.current_balance > 0 ? "text-amber-600" : "text-emerald-600"}`}>Balance Due</div>
                <div className={`font-bold ${ledgerData.summary.current_balance > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                  {formatCurrency(ledgerData.summary.current_balance)}
                </div>
              </div>
            </div>
          )}

          {/* Ledger table */}
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
            </div>
          ) : ledgerData?.entries?.length ? (
            <div className="overflow-x-auto border border-stone-200 rounded-xl">
              <table className="w-full text-sm">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500">Date</th>
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500">Type</th>
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500">Description</th>
                    <th className="text-right px-3 py-2.5 text-xs font-semibold text-stone-500">Debit</th>
                    <th className="text-right px-3 py-2.5 text-xs font-semibold text-stone-500">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {ledgerData.entries.map((entry, idx) => (
                    <tr key={idx} className="hover:bg-stone-50">
                      <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap">{entry.date}</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${entry.type === "invoice" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {entry.type}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-stone-700">{entry.description}</td>
                      <td className="px-3 py-2.5 text-right text-red-600 font-medium">
                        {entry.debit > 0 ? formatCurrency(entry.debit) : <span className="text-stone-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right text-emerald-600 font-medium">
                        {entry.credit > 0 ? formatCurrency(entry.credit) : <span className="text-stone-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 text-stone-400 text-sm">No ledger entries found</div>
          )}

          {/* Footer */}
          <div className="flex justify-end pt-1">
            <button
              onClick={downloadPdf}
              disabled={pdfGenerating || !ledgerData}
              className="flex items-center gap-2 px-4 py-2 bg-stone-800 hover:bg-stone-900 text-white rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
            >
              {pdfGenerating ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {pdfGenerating ? "Generating…" : "Download PDF"}
            </button>
          </div>
        </div>
      </Modal>

      {printLedger && (
        <PrintableLedger
          client={printLedger.client}
          entries={(printLedger as any).entries}
          summary={printLedger.summary}
          fromDate={(printLedger as any).fromDate}
          toDate={(printLedger as any).toDate}
          generatedAt={(printLedger as any).generatedAt}
          businessInfo={{
            name: settings?.business?.businessName || "Shalimar",
            logoUrl: settings?.logoUrl,
            address: settings?.business?.address,
            phone: settings?.business?.phone,
            email: settings?.business?.email,
          }}
        />
      )}
    </>
  );
}
